import { useCallback, useEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import type { Evaluation } from "@/types/evaluation";
import type { SubTemplateData } from "@/types/evaluation";
import { useEvaluationStore } from "@/store/useEvaluationStore";

type SaveStatus = "idle" | "saving" | "saved" | "error";

interface AutoSaveDeps {
  values: Record<string, unknown>;
  sectionToggles: Record<string, boolean>;
  fieldToggles: Record<string, boolean>;
  subTemplateValues: Record<string, SubTemplateData>;
  fillVariables: Record<string, string>;
  name: string;
  unifiedDocument?: Record<string, unknown> | null;
}

function buildEvalPayload(ev: Evaluation, d: AutoSaveDeps): Evaluation {
  return {
    ...ev,
    fieldToggles: d.fieldToggles,
    fillVariables: d.fillVariables,
    name: d.name,
    sectionToggles: d.sectionToggles,
    subTemplateValues: d.subTemplateValues,
    values: d.values,
    ...(d.unifiedDocument === undefined
      ? {}
      : { unifiedDocument: d.unifiedDocument as Record<string, unknown> | undefined }),
  };
}

function useStoreSaveRef() {
  const saveRef = useRef(useEvaluationStore.getState().save);
  useEffect(() => {
    return useEvaluationStore.subscribe((s) => {
      saveRef.current = s.save;
    });
  }, []);
  return saveRef;
}

function useFlushOnUnmountAndUnload(debouncedSave: { flush: () => unknown }) {
  useEffect(
    () => () => {
      debouncedSave.flush();
    },
    [],
  );
  useEffect(() => {
    const handler = () => {
      debouncedSave.flush();
    };
    window.addEventListener("beforeunload", handler);
    return () => {
      window.removeEventListener("beforeunload", handler);
    };
  }, []);
}

export function useAutoSave(evaluation: Evaluation | null, deps: AutoSaveDeps) {
  const [status, setStatus] = useState<SaveStatus>("idle");
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const isFirstRender = useRef(true);

  const evalRef = useRef(evaluation);
  const depsRef = useRef(deps);
  evalRef.current = evaluation;
  depsRef.current = deps;

  const saveRef = useStoreSaveRef();

  const doSave = useCallback(async () => {
    const ev = evalRef.current;
    const d = depsRef.current;
    if (!ev) {
      return;
    }
    setStatus("saving");
    try {
      await saveRef.current(buildEvalPayload(ev, d));
      setStatus("saved");
      setLastSaved(new Date());
    } catch {
      setStatus("error");
    }
  }, []);

  const debouncedSave = useDebouncedCallback(doSave, 1000);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!evalRef.current) {
      return;
    }
    void debouncedSave();
  }, [
    deps.values,
    deps.sectionToggles,
    deps.fieldToggles,
    deps.subTemplateValues,
    deps.fillVariables,
    deps.name,
    deps.unifiedDocument,
  ]);

  useFlushOnUnmountAndUnload(debouncedSave);

  return { cancel: debouncedSave.cancel, flush: debouncedSave.flush, lastSaved, status };
}
